import type { SearchRequestInput } from '@servicetim/shared';
import { useTicketSearch } from '@servicetim/ui/api';
import { Button, ProblemBanner } from '@servicetim/ui/components';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { StatusBadge } from './badges';
import { CreateTicketDialog } from './create-ticket-dialog';
import { RailCard } from './rail-card';
import { useTicketContext } from './ticket-context';
import { TicketKeyLink } from './ticket-link';

/** Children (`parent_id = this`) with a "create child" shortcut (S-05-10). */
export function SubTicketsCard() {
  const { ticket, statuses } = useTicketContext();
  const request = useMemo<SearchRequestInput>(
    () => ({
      filter: { field: 'parent_id', op: 'eq', value: ticket.id },
      sort: [{ field: 'created_at', dir: 'asc' }],
      select: ['id', 'key', 'summary', 'status_id'],
      page: { size: 50 },
    }),
    [ticket.id],
  );
  const children = useTicketSearch(request);
  const [creating, setCreating] = useState(false);

  return (
    <RailCard
      title={`Sub-tickets${children.isSuccess ? ` (${children.items.length}${children.hasMore ? '+' : ''})` : ''}`}
      actions={
        <Button size="sm" variant="ghost" onClick={() => setCreating(true)}>
          <Plus />
          Child
        </Button>
      }
    >
      {children.error ? <ProblemBanner error={children.error} size="sm" /> : null}
      {children.isSuccess && children.items.length === 0 ? (
        <p className="text-fg-muted">No sub-tickets.</p>
      ) : null}
      <ul className="space-y-2">
        {children.items.map((child) => {
          const status = child.statusId ? statuses.byId.get(child.statusId) : undefined;
          return (
            <li key={child.id} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <TicketKeyLink ticketKey={child.key} />
                <p className="truncate text-fg-muted" title={child.summary}>
                  {child.summary}
                </p>
              </div>
              <StatusBadge
                label={child.display?.status_id ?? status?.name}
                category={status?.category}
                color={status?.color}
              />
            </li>
          );
        })}
      </ul>
      {children.hasMore ? (
        <Button
          size="sm"
          variant="outline"
          onClick={children.loadMore}
          disabled={children.isLoadingMore}
        >
          Load more
        </Button>
      ) : null}
      <CreateTicketDialog
        open={creating}
        onOpenChange={setCreating}
        parent={{ id: ticket.id, key: ticket.key, projectId: ticket.projectId }}
      />
    </RailCard>
  );
}
